// @ts-nocheck
/**
 * ファンド管理システム
 * 購入、売却、維持コスト処理
 */

import { INVESTMENT } from '../../data/constants';
import { ALL_FUNDS, getFund } from '../data/funds';
import { getFundPrice } from './priceEngine';
import { createDebt, addToDebt, hasDebt } from './debtManager';

// ========================================
// 購入処理
// ========================================

/**
 * レバレッジが解放済みかチェック
 * @param {Object} investmentData - 投資データ
 * @param {number} leverage - レバレッジ倍率
 * @returns {boolean} 解放済みならtrue
 */
export function isLeverageUnlocked(investmentData, leverage) {
  const experience = investmentData.experienceCount || 0;
  const required = INVESTMENT.LEVERAGE_UNLOCK[leverage];
  return required !== undefined && experience >= required;
}

/**
 * レバレッジに応じた保有期限（日数）を取得
 * @param {number} leverage - レバレッジ倍率
 * @returns {number|null} 保有期限日数（nullは期限なし）
 */
export function getLeverageExpiryDays(leverage) {
  const expiry = INVESTMENT.LEVERAGE_EXPIRY[leverage];
  return expiry !== undefined ? expiry : INVESTMENT.MAX_HOLDING_DAYS;
}

/**
 * 解放済みレバレッジ一覧を取得
 * @param {Object} investmentData - 投資データ
 * @returns {number[]} 解放済みレバレッジ倍率の配列
 */
export function getUnlockedLeverages(investmentData) {
  return INVESTMENT.LEVERAGE_OPTIONS.filter((lev) => isLeverageUnlocked(investmentData, lev));
}

/**
 * ファンド購入の検証
 * @param {Object} investmentData - 投資データ
 * @param {number} gold - 所持金
 * @param {string} fundId - ファンドID
 * @param {number} units - 購入口数
 * @param {number} leverage - レバレッジ倍率（デフォルト1）
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Object} { valid: boolean, error?: string, cost?: number, fee?: number }
 */
export function validatePurchase(investmentData, gold, fundId, units, leverage = 1, marketIndex = null, marketState = null) {
  const fund = getFund(fundId);
  if (!fund) {
    return { valid: false, error: '存在しないファンドです' };
  }

  if (units < INVESTMENT.MIN_UNITS) {
    return { valid: false, error: `最低${INVESTMENT.MIN_UNITS}口から購入できます` };
  }

  // レバレッジ検証
  if (leverage > 1 && !isLeverageUnlocked(investmentData, leverage)) {
    return { valid: false, error: `${leverage}倍レバレッジは未解放です` };
  }

  // 現在の保有口数をチェック（レバレッジなしのみ合算）
  const currentHolding = investmentData.holdings.find(
    (h) => h.fundId === fundId && h.leverage === 1
  );
  const currentUnits = currentHolding ? currentHolding.units : 0;
  if (leverage === 1 && currentUnits + units > INVESTMENT.MAX_UNITS_PER_FUND) {
    return {
      valid: false,
      error: `1ファンドの最大保有口数は${INVESTMENT.MAX_UNITS_PER_FUND}口です`,
    };
  }

  // 借金中は投資不可（Step 6）
  if (hasDebt(investmentData.debt)) {
    return { valid: false, error: '借金を返済してから投資してください' };
  }

  // コスト計算（レバレッジ適用）
  const price = getFundPrice(fundId, marketIndex, marketState);
  const positionValue = price * units;  // 実質ポジション価値
  const margin = Math.floor(positionValue / leverage);  // 必要証拠金
  const fee = Math.floor(margin * INVESTMENT.PURCHASE_FEE_RATE) + Math.floor(margin * 0.005 * (leverage - 1));
  const totalCost = margin + fee;

  if (gold < totalCost) {
    return { valid: false, error: '所持金が不足しています', cost: totalCost, fee };
  }

  return {
    valid: true,
    cost: totalCost,
    fee,
    price,
    subtotal: margin,  // UIでは証拠金を表示
    positionValue,
    margin,
    leverage,
  };
}

/**
 * ファンドを購入
 * @param {Object} investmentData - 投資データ
 * @param {string} fundId - ファンドID
 * @param {number} units - 購入口数
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @param {Object} options - オプション { leverage, direction, stopLoss, takeProfit, marketIndex, marketState }
 * @returns {Object} { newInvestmentData, cost, fee }
 */
export function purchaseFund(investmentData, fundId, units, currentBattleNumber, options = {}) {
  const { leverage = 1, direction = 'long', stopLoss = null, takeProfit = null, marketIndex = null, marketState = null } = options;

  const price = getFundPrice(fundId, marketIndex, marketState);
  const positionValue = price * units;
  const margin = Math.floor(positionValue / leverage);
  const fee = Math.floor(margin * INVESTMENT.PURCHASE_FEE_RATE) + Math.floor(margin * 0.005 * (leverage - 1));
  const totalCost = margin + fee;

  // レバレッジポジションは常に新規（合算しない）
  // 現物（leverage=1）のみ既存保有に合算
  let newHoldings;
  if (leverage === 1) {
    const existingIndex = investmentData.holdings.findIndex(
      (h) => h.fundId === fundId && h.leverage === 1
    );

    if (existingIndex >= 0) {
      // 既存保有に追加（平均取得単価とmarginを更新）
      const existing = investmentData.holdings[existingIndex];
      const totalUnits = existing.units + units;
      const avgPrice = Math.floor(
        (existing.purchasePrice * existing.units + price * units) / totalUnits
      );
      // 現物のmarginは総ポジション価値（avgPrice × totalUnits）
      const newMargin = avgPrice * totalUnits;

      newHoldings = [...investmentData.holdings];
      newHoldings[existingIndex] = {
        ...existing,
        units: totalUnits,
        purchasePrice: avgPrice,
        margin: newMargin,
      };
    } else {
      // 新規現物保有（期限なし）
      const newHolding = {
        id: `holding_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        fundId,
        units,
        purchasePrice: price,
        purchaseDate: currentBattleNumber,
        expiryDate: null,  // 現物は期限なし（長期保有可能）
        leverage: 1,
        direction: 'long',
        margin: positionValue,  // 現物は証拠金=ポジション価値
        stopLoss: null,
        takeProfit: null,
      };
      newHoldings = [...investmentData.holdings, newHolding];
    }
  } else {
    // レバレッジポジションは常に新規作成
    // レバレッジに応じた保有期限を設定（高レバレッジほど短期）
    const expiryDays = INVESTMENT.LEVERAGE_EXPIRY[leverage] || INVESTMENT.MAX_HOLDING_DAYS;
    const newHolding = {
      id: `holding_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      fundId,
      units,
      purchasePrice: price,
      purchaseDate: currentBattleNumber,
      expiryDate: currentBattleNumber + expiryDays,
      leverage,
      direction,
      margin,  // 投入した証拠金
      stopLoss,
      takeProfit,
    };
    newHoldings = [...investmentData.holdings, newHolding];
  }

  // 取引履歴に追加
  const historyEntry = {
    date: currentBattleNumber,
    action: 'buy',
    type: 'fund',
    fundId,
    units,
    price,
    fee,
    leverage,
    margin,
  };
  const newHistory = [...investmentData.history, historyEntry].slice(-100); // 最新100件

  // 統計更新
  const newStats = {
    ...investmentData.totalStats,
    totalInvestments: investmentData.totalStats.totalInvestments + 1,
    totalInvestedAmount: investmentData.totalStats.totalInvestedAmount + margin,
    totalFeesPaid: investmentData.totalStats.totalFeesPaid + fee,
  };

  return {
    newInvestmentData: {
      ...investmentData,
      holdings: newHoldings,
      history: newHistory,
      experienceCount: investmentData.experienceCount + 1,
      totalStats: newStats,
    },
    cost: totalCost,
    fee,
    margin,
    positionValue,
    leverage,
  };
}

// ========================================
// 売却処理
// ========================================

/**
 * ファンド売却の検証
 * @param {Object} investmentData - 投資データ
 * @param {string} fundIdOrHoldingId - ファンドIDまたは保有ID
 * @param {number} units - 売却口数
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Object} { valid: boolean, error?: string, revenue?: number, fee?: number, pnl?: number }
 */
export function validateSell(investmentData, fundIdOrHoldingId, units, marketIndex = null, marketState = null) {
  // 保有IDで検索（レバレッジポジション用）
  let holding = investmentData.holdings.find((h) => h.id === fundIdOrHoldingId);

  // 見つからなければファンドIDで検索（現物用、後方互換）
  if (!holding) {
    holding = investmentData.holdings.find(
      (h) => h.fundId === fundIdOrHoldingId && h.leverage === 1
    );
  }

  if (!holding) {
    return { valid: false, error: 'このファンドを保有していません' };
  }

  if (units > holding.units) {
    return { valid: false, error: `保有口数(${holding.units}口)を超えています` };
  }

  if (units < 1) {
    return { valid: false, error: '1口以上から売却できます' };
  }

  // 収益計算
  const currentPrice = getFundPrice(holding.fundId, marketIndex, marketState);
  const leverage = holding.leverage || 1;
  const direction = holding.direction || 'long';

  // ポジション損益計算
  let priceDiff;
  if (direction === 'long') {
    priceDiff = currentPrice - holding.purchasePrice;
  } else {
    priceDiff = holding.purchasePrice - currentPrice;  // ショートは逆
  }

  const positionPnl = priceDiff * units;  // レバレッジ関係なく単純な差額
  const marginUsed = Math.floor((holding.purchasePrice * units) / leverage);

  // 手数料は証拠金ベース
  const fee = Math.floor(marginUsed * INVESTMENT.SELL_FEE_RATE);

  // 返却額 = 証拠金 + 損益 - 手数料
  const revenue = marginUsed + positionPnl - fee;
  const pnl = positionPnl - fee;  // 純損益（手数料控除後）

  return {
    valid: true,
    revenue: Math.max(0, revenue),  // マイナスにはならない（最悪0）
    fee,
    pnl,
    currentPrice,
    gross: currentPrice * units,  // 参考: 現物ベースの価値
    leverage,
    direction,
    marginUsed,
    positionPnl,
    holdingId: holding.id,
  };
}

/**
 * ファンドを売却
 * @param {Object} investmentData - 投資データ
 * @param {string} fundIdOrHoldingId - ファンドIDまたは保有ID
 * @param {number} units - 売却口数
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Object} { newInvestmentData, revenue, fee, pnl }
 */
export function sellFund(investmentData, fundIdOrHoldingId, units, currentBattleNumber, marketIndex = null, marketState = null) {
  // 保有IDで検索（レバレッジポジション用）
  let holding = investmentData.holdings.find((h) => h.id === fundIdOrHoldingId);

  // 見つからなければファンドIDで検索（現物用、後方互換）
  if (!holding) {
    holding = investmentData.holdings.find(
      (h) => h.fundId === fundIdOrHoldingId && h.leverage === 1
    );
  }

  const currentPrice = getFundPrice(holding.fundId, marketIndex, marketState);
  const leverage = holding.leverage || 1;
  const direction = holding.direction || 'long';

  // ポジション損益計算
  let priceDiff;
  if (direction === 'long') {
    priceDiff = currentPrice - holding.purchasePrice;
  } else {
    priceDiff = holding.purchasePrice - currentPrice;
  }

  const positionPnl = priceDiff * units;
  const marginUsed = Math.floor((holding.purchasePrice * units) / leverage);
  const fee = Math.floor(marginUsed * INVESTMENT.SELL_FEE_RATE);
  const revenue = Math.max(0, marginUsed + positionPnl - fee);
  const pnl = positionPnl - fee;

  // 保有を更新
  let newHoldings;
  if (units >= holding.units) {
    // 全売却（保有IDで削除）
    newHoldings = investmentData.holdings.filter((h) => h.id !== holding.id);
  } else {
    // 部分売却
    newHoldings = investmentData.holdings.map((h) => {
      if (h.id === holding.id) {
        return { ...h, units: h.units - units };
      }
      return h;
    });
  }

  // 取引履歴に追加
  const historyEntry = {
    date: currentBattleNumber,
    action: 'sell',
    type: 'fund',
    fundId: holding.fundId,
    units,
    price: currentPrice,
    fee,
    pnl,
    leverage,
    direction,
  };
  const newHistory = [...investmentData.history, historyEntry].slice(-100);

  // 統計更新
  const newStats = {
    ...investmentData.totalStats,
    totalProfitableTrades:
      investmentData.totalStats.totalProfitableTrades + (pnl > 0 ? 1 : 0),
    totalPayout: investmentData.totalStats.totalPayout + revenue,
    totalProfit: investmentData.totalStats.totalProfit + pnl,
    totalFeesPaid: investmentData.totalStats.totalFeesPaid + fee,
  };

  return {
    newInvestmentData: {
      ...investmentData,
      holdings: newHoldings,
      history: newHistory,
      totalStats: newStats,
    },
    revenue,
    fee,
    pnl,
    leverage,
    direction,
  };
}

// ========================================
// 維持コスト処理
// ========================================

/**
 * 日次維持コストを計算
 * @param {Object} investmentData - 投資データ
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {number} 維持コスト合計
 */
export function calculateDailyMaintenanceCost(investmentData, marketIndex = null, marketState = null) {
  let totalCost = 0;

  for (const holding of investmentData.holdings) {
    const currentPrice = getFundPrice(holding.fundId, marketIndex, marketState);
    const value = currentPrice * holding.units;
    const cost = Math.floor(value * INVESTMENT.MAINTENANCE_FEE_RATE);
    totalCost += cost;
  }

  return totalCost;
}

/**
 * 維持コストを適用
 * @param {Object} investmentData - 投資データ
 * @param {number} gold - 所持金
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Object} { newInvestmentData, newGold, maintenanceCost }
 */
export function applyMaintenanceCost(investmentData, gold, marketIndex = null, marketState = null) {
  const maintenanceCost = calculateDailyMaintenanceCost(investmentData, marketIndex, marketState);

  // 維持コストが所持金を超える場合は所持金分だけ引く
  const actualCost = Math.min(maintenanceCost, gold);

  return {
    newInvestmentData: {
      ...investmentData,
      totalMaintenancePaid: investmentData.totalMaintenancePaid + actualCost,
    },
    newGold: gold - actualCost,
    maintenanceCost: actualCost,
  };
}

// ========================================
// 期限切れ処理
// ========================================

/**
 * 期限切れファンドを強制売却
 * @param {Object} investmentData - 投資データ
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Object} { newInvestmentData, forceSoldItems, totalRevenue }
 */
export function processExpiredHoldings(investmentData, currentBattleNumber, marketIndex = null, marketState = null) {
  // 期限なし（expiryDate === null）の現物は除外
  const expiredHoldings = investmentData.holdings.filter(
    (h) => h.expiryDate !== null && h.expiryDate <= currentBattleNumber
  );

  if (expiredHoldings.length === 0) {
    return { newInvestmentData: investmentData, forceSoldItems: [], totalRevenue: 0 };
  }

  let newInvestmentData = { ...investmentData };
  const forceSoldItems = [];
  let totalRevenue = 0;

  for (const holding of expiredHoldings) {
    const { newInvestmentData: updated, revenue, pnl } = sellFund(
      newInvestmentData,
      holding.id,  // 保有IDを使用
      holding.units,
      currentBattleNumber,
      marketIndex,
      marketState
    );
    newInvestmentData = updated;
    totalRevenue += revenue;

    forceSoldItems.push({
      fundId: holding.fundId,
      holdingId: holding.id,
      units: holding.units,
      revenue,
      pnl,
      leverage: holding.leverage,
    });
  }

  return { newInvestmentData, forceSoldItems, totalRevenue };
}

/**
 * レバレッジポジションのロスカット処理
 * @param {Object} investmentData - 投資データ
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Object} { newInvestmentData, liquidatedItems, totalLoss, debtCreated }
 */
export function processLeverageLiquidations(investmentData, currentBattleNumber, marketIndex = null, marketState = null) {
  // レバレッジポジションのみ対象
  const leveragedHoldings = investmentData.holdings.filter((h) => h.leverage > 1);

  const liquidatedItems = [];
  let newInvestmentData = { ...investmentData };
  let totalLoss = 0;
  let totalDebt = 0;

  for (const holding of leveragedHoldings) {
    const currentPrice = getFundPrice(holding.fundId, marketIndex, marketState);
    const direction = holding.direction || 'long';

    // 損益計算
    let priceDiff;
    if (direction === 'long') {
      priceDiff = currentPrice - holding.purchasePrice;
    } else {
      priceDiff = holding.purchasePrice - currentPrice;
    }

    const positionPnl = priceDiff * holding.units;
    const marginValue = holding.margin + positionPnl;  // 現在の証拠金価値
    const marginRatio = marginValue / holding.margin;  // 証拠金維持率

    // ロスカット判定（証拠金維持率が閾値を下回った場合）
    if (marginRatio < INVESTMENT.LIQUIDATION_THRESHOLD) {
      const { newInvestmentData: updated, revenue, pnl } = sellFund(
        newInvestmentData,
        holding.id,
        holding.units,
        currentBattleNumber,
        marketIndex,
        marketState
      );
      newInvestmentData = updated;
      totalLoss += Math.abs(Math.min(0, pnl));

      // 証拠金を超える損失 = 借金発生
      // 損失が証拠金より大きい場合（revenue = 0 かつ pnl < -margin）
      const excessLoss = Math.abs(pnl) - holding.margin;
      const debtAmount = excessLoss > 0 ? excessLoss : 0;
      if (debtAmount > 0) {
        totalDebt += debtAmount;
      }

      liquidatedItems.push({
        fundId: holding.fundId,
        holdingId: holding.id,
        units: holding.units,
        leverage: holding.leverage,
        direction,
        marginRatio: (marginRatio * 100).toFixed(1),
        pnl,
        revenue,
        debtAmount,
      });
    }
  }

  // 借金が発生した場合、debtを更新
  if (totalDebt > 0) {
    if (hasDebt(newInvestmentData.debt)) {
      newInvestmentData = {
        ...newInvestmentData,
        debt: addToDebt(newInvestmentData.debt, totalDebt, currentBattleNumber),
      };
    } else {
      newInvestmentData = {
        ...newInvestmentData,
        debt: createDebt(totalDebt, currentBattleNumber, 'leverage'),
      };
    }
  }

  return { newInvestmentData, liquidatedItems, totalLoss, debtCreated: totalDebt };
}

/**
 * 保有ポジションの証拠金維持率を計算
 * @param {Object} investmentData - 投資データ
 * @param {string} holdingId - 保有ID
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Object|null} { marginRatio, currentValue, pnl, isWarning, isLiquidation }
 */
export function getMarginStatus(investmentData, holdingId, marketIndex = null, marketState = null) {
  const holding = investmentData.holdings.find((h) => h.id === holdingId);
  if (!holding || holding.leverage <= 1) return null;

  const currentPrice = getFundPrice(holding.fundId, marketIndex, marketState);
  const direction = holding.direction || 'long';

  let priceDiff;
  if (direction === 'long') {
    priceDiff = currentPrice - holding.purchasePrice;
  } else {
    priceDiff = holding.purchasePrice - currentPrice;
  }

  const positionPnl = priceDiff * holding.units;
  const marginValue = holding.margin + positionPnl;
  const marginRatio = marginValue / holding.margin;

  return {
    marginRatio,
    marginValue,
    pnl: positionPnl,
    isWarning: marginRatio < 1.0,  // 100%以下で警告
    isLiquidation: marginRatio < INVESTMENT.LIQUIDATION_THRESHOLD,  // ロスカット水準
  };
}

// ========================================
// 自動注文処理（SL/TP）
// ========================================

/**
 * 自動注文（ストップロス/テイクプロフィット）を処理
 * @param {Object} investmentData - 投資データ
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Object} { newInvestmentData, executedOrders, totalPnl }
 */
export function processAutoOrders(investmentData, currentBattleNumber, marketIndex = null, marketState = null) {
  const executedOrders = [];
  let newInvestmentData = { ...investmentData };
  let totalPnl = 0;

  // 全ポジションをスキャン
  const holdingsToCheck = [...investmentData.holdings];

  for (const holding of holdingsToCheck) {
    const currentPrice = getFundPrice(holding.fundId, marketIndex, marketState);
    const leverage = holding.leverage || 1;
    const direction = holding.direction || 'long';

    // 損益率計算
    let priceDiff;
    if (direction === 'long') {
      priceDiff = currentPrice - holding.purchasePrice;
    } else {
      priceDiff = holding.purchasePrice - currentPrice;
    }
    const pnlPercent = (priceDiff / holding.purchasePrice) * 100;

    // ストップロスチェック
    if (holding.stopLoss !== null && pnlPercent <= -Math.abs(holding.stopLoss)) {
      const { newInvestmentData: updated, revenue, pnl } = sellFund(
        newInvestmentData,
        holding.id,
        holding.units,
        currentBattleNumber,
        marketIndex,
        marketState
      );
      newInvestmentData = updated;
      totalPnl += pnl;

      executedOrders.push({
        type: 'stopLoss',
        holdingId: holding.id,
        fundId: holding.fundId,
        units: holding.units,
        leverage,
        direction,
        triggerPercent: holding.stopLoss,
        actualPercent: pnlPercent.toFixed(1),
        pnl,
        revenue,
      });
      continue;  // このポジションは決済済みなのでTPチェック不要
    }

    // テイクプロフィットチェック
    if (holding.takeProfit !== null && pnlPercent >= holding.takeProfit) {
      const { newInvestmentData: updated, revenue, pnl } = sellFund(
        newInvestmentData,
        holding.id,
        holding.units,
        currentBattleNumber,
        marketIndex,
        marketState
      );
      newInvestmentData = updated;
      totalPnl += pnl;

      executedOrders.push({
        type: 'takeProfit',
        holdingId: holding.id,
        fundId: holding.fundId,
        units: holding.units,
        leverage,
        direction,
        triggerPercent: holding.takeProfit,
        actualPercent: pnlPercent.toFixed(1),
        pnl,
        revenue,
      });
    }
  }

  return { newInvestmentData, executedOrders, totalPnl };
}

/**
 * 保有ポジションのSL/TPを更新
 * @param {Object} investmentData - 投資データ
 * @param {string} holdingId - 保有ID
 * @param {number|null} stopLoss - ストップロス（%、nullで解除）
 * @param {number|null} takeProfit - テイクプロフィット（%、nullで解除）
 * @returns {Object} { newInvestmentData }
 */
export function updateAutoOrders(investmentData, holdingId, stopLoss, takeProfit) {
  const newHoldings = investmentData.holdings.map((h) => {
    if (h.id === holdingId) {
      return { ...h, stopLoss, takeProfit };
    }
    return h;
  });

  return {
    newInvestmentData: {
      ...investmentData,
      holdings: newHoldings,
    },
  };
}

// ========================================
// 保有情報取得
// ========================================

/**
 * ファンドの保有情報を取得
 * @param {Object} investmentData - 投資データ
 * @param {string} fundId - ファンドID
 * @returns {Object|null} 保有情報
 */
export function getHolding(investmentData, fundId) {
  return investmentData.holdings.find((h) => h.fundId === fundId) || null;
}

/**
 * 保有ファンドの現在評価額を計算
 * @param {Object} investmentData - 投資データ
 * @param {string} fundId - ファンドID
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Object|null} { value, pnl, pnlPercent }
 */
export function getHoldingValue(investmentData, fundId, marketIndex = null, marketState = null) {
  const holding = getHolding(investmentData, fundId);
  if (!holding) return null;

  const currentPrice = getFundPrice(fundId, marketIndex, marketState);
  const direction = holding.direction || 'long';

  // ポジション損益計算（ショートは逆）
  let priceDiff;
  if (direction === 'long') {
    priceDiff = currentPrice - holding.purchasePrice;
  } else {
    priceDiff = holding.purchasePrice - currentPrice;
  }
  const pnl = priceDiff * holding.units;
  const cost = holding.purchasePrice * holding.units;
  const pnlPercent = cost > 0 ? (pnl / cost) * 100 : 0;

  // valueは証拠金 + 損益（ショートの場合も正しく計算）
  const margin = holding.margin || cost;
  const value = margin + pnl;

  return { value, pnl, pnlPercent, currentPrice, direction };
}

/**
 * 全保有ファンドの合計評価額を計算
 * @param {Object} investmentData - 投資データ
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Object} { totalValue, totalCost, totalPnl, totalPnlPercent }
 */
export function getTotalPortfolioValue(investmentData, marketIndex = null, marketState = null) {
  let totalValue = 0;
  let totalCost = 0;
  let totalPnl = 0;
  const holdings = investmentData.holdings || [];

  for (const holding of holdings) {
    const currentPrice = getFundPrice(holding.fundId, marketIndex, marketState);
    const direction = holding.direction || 'long';

    // ポジション損益計算（ショートは逆）
    let priceDiff;
    if (direction === 'long') {
      priceDiff = currentPrice - holding.purchasePrice;
    } else {
      priceDiff = holding.purchasePrice - currentPrice;
    }
    const pnl = priceDiff * holding.units;
    const cost = holding.purchasePrice * holding.units;
    const margin = holding.margin || cost;

    totalCost += margin;  // 証拠金ベース
    totalValue += margin + pnl;  // 証拠金 + 損益
    totalPnl += pnl;
  }

  const totalPnlPercent = totalCost > 0 ? (totalPnl / totalCost) * 100 : 0;

  return { totalValue, totalCost, totalPnl, totalPnlPercent };
}

// ========================================
// 配当システム
// ========================================

/**
 * 保有期間に応じた配当率を取得
 * @param {number} holdingDays - 保有日数
 * @returns {number} 配当率（0 = 配当なし）
 */
export function getDividendRate(holdingDays) {
  const { DIVIDEND_RATES } = INVESTMENT;

  // TIER_2（30日以上）をチェック
  if (DIVIDEND_RATES.TIER_2.minDays <= holdingDays) {
    return DIVIDEND_RATES.TIER_2.rate;
  }

  // TIER_1（10-29日）をチェック
  if (DIVIDEND_RATES.TIER_1.minDays <= holdingDays && holdingDays <= DIVIDEND_RATES.TIER_1.maxDays) {
    return DIVIDEND_RATES.TIER_1.rate;
  }

  // 9日以下は配当なし
  return 0;
}

/**
 * 保有ポジションの配当を計算
 * @param {Object} holding - 保有ポジション
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Object} { dividend, rate, holdingDays }
 */
export function calculateDividend(holding, currentBattleNumber, marketIndex = null, marketState = null) {
  // レバレッジポジションは配当なし
  if (holding.leverage > 1) {
    return { dividend: 0, rate: 0, holdingDays: 0 };
  }

  const holdingDays = currentBattleNumber - holding.purchaseDate;
  const rate = getDividendRate(holdingDays);

  if (rate === 0) {
    return { dividend: 0, rate: 0, holdingDays };
  }

  // 配当 = 現在評価額 × 配当率
  const currentPrice = getFundPrice(holding.fundId, marketIndex, marketState);
  const currentValue = currentPrice * holding.units;
  const dividend = Math.floor(currentValue * rate);

  return { dividend, rate, holdingDays };
}

/**
 * 全保有ポジションの配当を計算・付与
 * @param {Object} investmentData - 投資データ
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @param {Object} marketIndex - マーケットインデックス（オプション）
 * @param {Object} marketState - 市場状態（オプション）
 * @returns {Object} { newInvestmentData, totalDividend, dividendDetails }
 */
export function processDividends(investmentData, currentBattleNumber, marketIndex = null, marketState = null) {
  const dividendDetails = [];
  let totalDividend = 0;

  for (const holding of investmentData.holdings) {
    const { dividend, rate, holdingDays } = calculateDividend(
      holding,
      currentBattleNumber,
      marketIndex,
      marketState
    );

    if (dividend > 0) {
      totalDividend += dividend;
      dividendDetails.push({
        holdingId: holding.id,
        fundId: holding.fundId,
        units: holding.units,
        holdingDays,
        rate,
        dividend,
      });
    }
  }

  // 統計更新（配当総額を追跡）
  const newStats = {
    ...investmentData.totalStats,
    totalDividendsReceived: (investmentData.totalStats.totalDividendsReceived || 0) + totalDividend,
  };

  return {
    newInvestmentData: {
      ...investmentData,
      totalStats: newStats,
    },
    totalDividend,
    dividendDetails,
  };
}

/**
 * 保有ポジションの配当ティア情報を取得（UI表示用）
 * @param {Object} holding - 保有ポジション
 * @param {number} currentBattleNumber - 現在の戦闘番号
 * @returns {Object} { tier, rate, holdingDays, daysToNextTier, nextTierRate }
 */
export function getDividendTierInfo(holding, currentBattleNumber) {
  const { DIVIDEND_RATES } = INVESTMENT;
  const holdingDays = currentBattleNumber - holding.purchaseDate;

  // レバレッジポジションは配当なし
  if (holding.leverage > 1) {
    return {
      tier: 'なし',
      rate: 0,
      holdingDays,
      daysToNextTier: null,
      nextTierRate: null,
    };
  }

  // TIER_2（30日以上）
  if (holdingDays >= DIVIDEND_RATES.TIER_2.minDays) {
    return {
      tier: '長期',
      rate: DIVIDEND_RATES.TIER_2.rate,
      holdingDays,
      daysToNextTier: null,  // 最高ティア
      nextTierRate: null,
    };
  }

  // TIER_1（10-29日）
  if (holdingDays >= DIVIDEND_RATES.TIER_1.minDays) {
    return {
      tier: '中期',
      rate: DIVIDEND_RATES.TIER_1.rate,
      holdingDays,
      daysToNextTier: DIVIDEND_RATES.TIER_2.minDays - holdingDays,
      nextTierRate: DIVIDEND_RATES.TIER_2.rate,
    };
  }

  // 配当なし（9日以下）
  return {
    tier: '短期',
    rate: 0,
    holdingDays,
    daysToNextTier: DIVIDEND_RATES.TIER_1.minDays - holdingDays,
    nextTierRate: DIVIDEND_RATES.TIER_1.rate,
  };
}

export default {
  isLeverageUnlocked,
  getUnlockedLeverages,
  getLeverageExpiryDays,
  validatePurchase,
  purchaseFund,
  validateSell,
  sellFund,
  calculateDailyMaintenanceCost,
  applyMaintenanceCost,
  processExpiredHoldings,
  processLeverageLiquidations,
  getMarginStatus,
  processAutoOrders,
  updateAutoOrders,
  getHolding,
  getHoldingValue,
  getTotalPortfolioValue,
  getDividendRate,
  calculateDividend,
  processDividends,
  getDividendTierInfo,
};
